import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchFilteredProducts } from "../actions/FilterActions";
import ProductCard from "./ProductCard";
import { ProductGridSkeleton } from "./HomeSkeletons";
import Pagination from "./Pagination";
import "../styles/searchresults.css";
import "../styles/productlist.css";
import "../styles/skeletons.css";

const SearchResults = ({
  filters,
  currentPage,
  totalPages,
  productsPerPage,
  setCurrentPage,
  filteredProducts,
  loading,
  query,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { categories } = useSelector((state) => state.categories);
  const [sortOption, setSortOption] = useState("new");

  const handleProductClick = (product) => {
    navigate(`/product/${product.slug || product.product_id}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Recursive function to find category by ID
  const findCategoryById = (catList, targetId) => {
    if (!catList || !Array.isArray(catList)) return null;
    for (const cat of catList) {
      if (
        cat.category_id === targetId ||
        cat.category_id?.toString() === targetId?.toString()
      ) {
        return cat;
      }
      if (
        cat.children &&
        Array.isArray(cat.children) &&
        cat.children.length > 0
      ) {
        const found = findCategoryById(cat.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  // Get selected category names from filters
  const getSelectedCategoryNames = () => {
    if (
      !filters?.category_id ||
      !Array.isArray(filters.category_id) ||
      filters.category_id.length === 0
    ) {
      return [];
    }

    // Helper function to check if a category ID is a child of any selected parent
    const isChildOfSelectedParent = (categoryId) => {
      for (const selectedId of filters.category_id) {
        if (selectedId === categoryId) continue;
        const selectedCategory = findCategoryById(categories, selectedId);
        if (selectedCategory) {
          const collectAllCategoryIds = (category) => {
            const ids = [category.category_id];
            if (
              category.children &&
              Array.isArray(category.children) &&
              category.children.length > 0
            ) {
              category.children.forEach((child) => {
                ids.push(...collectAllCategoryIds(child));
              });
            }
            return ids;
          };
          const allDescendants = collectAllCategoryIds(selectedCategory);
          if (allDescendants.includes(categoryId)) {
            return true;
          }
        }
      }
      return false;
    };

    // Only get top-level selected categories (not nested ones)
    const topLevelSelected = filters.category_id.filter(
      (categoryId) => !isChildOfSelectedParent(categoryId),
    );

    return topLevelSelected
      .map((categoryId) => {
        const category = findCategoryById(categories, categoryId);
        return category ? category.name : null;
      })
      .filter(Boolean);
  };

  // Get the title based on query, brand, or selected categories
  const getTitle = () => {
    const brand = filters?.brand || "";
    const selectedNames = getSelectedCategoryNames();

    // Priority: Query > Brand > Categories > All Products

    if (query) {
      // If there's a query, show it first
      const parts = [];
      parts.push(`Search Results for "${query}"`);

      if (brand && brand.trim()) {
        parts.push(
          <span key="brand" className="brand-highlight">
            "{brand}"
          </span>,
        );
      }
      if (selectedNames.length > 0) {
        parts.push(selectedNames.join(", "));
      }

      if (parts.length > 1) {
        return (
          <>
            {parts[0]} -{" "}
            {parts
              .slice(1)
              .map((part, index) =>
                typeof part === "string" ? (
                  <span key={index}>{part}</span>
                ) : (
                  part
                ),
              )}
          </>
        );
      }
      return parts[0];
    }

    // No query, check for brand
    if (brand && brand.trim()) {
      if (selectedNames.length > 0) {
        if (selectedNames.length === 1) {
          return (
            <>
              <span className="brand-highlight">"{brand}"</span> -{" "}
              {selectedNames[0]} Products
            </>
          );
        } else {
          return (
            <>
              <span className="brand-highlight">"{brand}"</span> -{" "}
              {selectedNames.join(", ")} Products
            </>
          );
        }
      }
      return (
        <>
          <span className="brand-highlight">"{brand}"</span> Products
        </>
      );
    }

    // No query or brand, check categories
    if (selectedNames.length > 0) {
      if (selectedNames.length === 1) {
        return `${selectedNames[0]} Products`;
      } else {
        return `${selectedNames.join(", ")} Products`;
      }
    }

    return "All Products";
  };

  // Get all unique category names from products
  const getCategories = () => {
    if (!filteredProducts || filteredProducts.length === 0) return "";

    const categoryNames = filteredProducts
      .filter((product) => product.category && product.category.name)
      .map((product) => product.category.name);

    const distinctCategoryNames = [...new Set(categoryNames)];
    return distinctCategoryNames.join(" • ");
  };

  // Sort products based on the selected option
  const sortedProducts = useMemo(() => {
    if (!filteredProducts.length) return [];

    return [...filteredProducts].sort((a, b) => {
      switch (sortOption) {
        case "price-asc":
          return parseFloat(a.base_price || 0) - parseFloat(b.base_price || 0);
        case "price-desc":
          return parseFloat(b.base_price || 0) - parseFloat(a.base_price || 0);
        case "best":
          // Show products with "BEST SELLER" tag first
          const aIsBest = a.tags && a.tags.includes("BEST SELLER") ? 1 : 0;
          const bIsBest = b.tags && b.tags.includes("BEST SELLER") ? 1 : 0;
          if (aIsBest !== bIsBest) return bIsBest - aIsBest;

          // Fall back to product_rating if tags are the same
          return (b.product_rating || 0) - (a.product_rating || 0);
        case "new":
        default:
          // Show products with "NEW ARRIVAL" tag first
          const aIsNew = a.tags && a.tags.includes("NEW ARRIVAL") ? 1 : 0;
          const bIsNew = b.tags && b.tags.includes("NEW ARRIVAL") ? 1 : 0;
          if (aIsNew !== bIsNew) return bIsNew - aIsNew;

          // Fall back to created_at date as secondary sort
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });
  }, [filteredProducts, sortOption]);

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  return (
    <div className="products">
      <div className="list-header">
        <div className="list-header-title">
          <h2>
            <b>{getTitle()}</b>
          </h2>
          <div className="subcategory-tags">
            {loading ? null : getCategories()}
          </div>
        </div>
        <div className="sort-options">
          <div>
            {loading ? (
              <>
                <span className="home-skeleton list-skeleton-meta" />
                <span className="home-skeleton list-skeleton-meta" />
              </>
            ) : (
              <>
                <span>{filteredProducts.length} items</span>
                <span>|</span>
                <span>
                  Page {currentPage} of {totalPages}
                </span>
              </>
            )}
          </div>
          {/* Sorting options */}
          <select
            value={sortOption}
            onChange={handleSortChange}
            disabled={loading}
          >
            <option value="new">New</option>
            <option value="best">Best</option>
            <option value="price-asc">Price: Low to High</option>
            <option value="price-desc">Price: High to Low</option>
          </select>
        </div>
      </div>

      {loading ? (
        <ProductGridSkeleton count={8} />
      ) : filteredProducts.length > 0 ? (
        <div className="products-grid">
          {sortedProducts.map((product) => (
            <div
              key={product.product_id}
              onClick={() => handleProductClick(product)}
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      ) : (
        <div className="no-products">
          <p>No products available</p>
        </div>
      )}

      {!loading && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
};

export default SearchResults;
